/* eslint-disable react-refresh/only-export-components -- библиотека иконок, не SPA-компонент */
import React from 'react';

/** Общие пропсы для всех SVG-иконок */
export interface IconProps extends React.SVGAttributes<SVGSVGElement> {
	/** Размер иконки в пикселях (ширина = высота) */
	size?: number;
	/** Цвет stroke / fill — по умолчанию наследуется от родителя */
	color?: string;
	className?: string;
	style?: React.CSSProperties;
}

/** Пропсы для точки-индикатора (Dot) */
export interface DotProps extends React.HTMLAttributes<HTMLSpanElement> {
	size?: number;
	color?: string;
	/** Добавить мягкую тень-свечение вокруг точки */
	glow?: boolean;
	style?: React.CSSProperties;
}

/**
 * Хелпер: оборачивает JSX-содержимое (paths, circles и т.д.)
 * в унифицированную SVG-обёртку 20×20 со stroke-стилем.
 */
const I = (paths: React.ReactNode, viewBox = '0 0 20 20'): React.FC<IconProps> => {
	const IconComponent: React.FC<IconProps> = ({
		size = 20,
		color = 'currentColor',
		style,
		...props
	}) => (
		<svg
			width={size}
			height={size}
			viewBox={viewBox}
			fill="none"
			stroke={color}
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
			style={{ flexShrink: 0, ...style }}
			{...props}
		>
			{paths}
		</svg>
	);
	return IconComponent;
};

/* ────────────────────────────────────────────
 * Навигация
 * ──────────────────────────────────────────── */

const Search = I(
	<>
		<circle cx="8.5" cy="8.5" r="5.5" />
		<path d="M13 13l4 4" />
	</>,
);

const Close = I(<path d="M5 5l10 10M15 5L5 15" />);

const Menu = I(<path d="M3 5h14M3 10h14M3 15h14" />);

const ChevronDown = I(<path d="M5 7.5l5 5 5-5" />);

const ChevronUp = I(<path d="M5 12.5l5-5 5 5" />);

const ChevronRight = I(<path d="M7.5 5l5 5-5 5" />);

const ChevronLeft = I(<path d="M12.5 5l-5 5 5 5" />);

const ArrowUp = I(
	<>
		<path d="M10 16V4" />
		<path d="M5 9l5-5 5 5" />
	</>,
);

const ArrowDown = I(
	<>
		<path d="M10 4v12" />
		<path d="M5 11l5 5 5-5" />
	</>,
);

const ArrowRight = I(
	<>
		<path d="M4 10h12" />
		<path d="M11 5l5 5-5 5" />
	</>,
);

const ArrowLeft = I(
	<>
		<path d="M16 10H4" />
		<path d="M9 5L4 10l5 5" />
	</>,
);

const ExternalLink = I(
	<>
		<path d="M11 3h6v6" />
		<path d="M17 3L8 12" />
		<path d="M15 11v5a1 1 0 01-1 1H4a1 1 0 01-1-1V6a1 1 0 011-1h5" />
	</>,
);

/* ────────────────────────────────────────────
 * Действия
 * ──────────────────────────────────────────── */

const Plus = I(<path d="M10 4v12M4 10h12" />);

const Minus = I(<path d="M4 10h12" />);

const Edit = I(
	<>
		<path d="M12 3l5 5-9 9H3v-5z" />
		<path d="M10.5 4.5l5 5" />
	</>,
);

const Trash = I(
	<>
		<path d="M5 5h10" />
		<path d="M8 5V3h4v2" />
		<path d="M6 5v11a1 1 0 001 1h6a1 1 0 001-1V5" />
		<path d="M8 8v6M12 8v6" />
	</>,
);

const Copy = I(
	<>
		<rect x="6" y="6" width="10" height="11" rx="1" />
		<path d="M4 14V4a1 1 0 011-1h9" />
	</>,
);

const Download = I(
	<>
		<path d="M10 3v10" />
		<path d="M6 10l4 4 4-4" />
		<path d="M3 15v1a1 1 0 001 1h12a1 1 0 001-1v-1" />
	</>,
);

const Upload = I(
	<>
		<path d="M10 13V3" />
		<path d="M6 6l4-4 4 4" />
		<path d="M3 15v1a1 1 0 001 1h12a1 1 0 001-1v-1" />
	</>,
);

const Refresh = I(
	<>
		<path d="M3 10a7 7 0 0113.4-2.8" />
		<path d="M17 3v4h-4" />
		<path d="M17 10A7 7 0 013.6 12.8" />
		<path d="M3 17v-4h4" />
	</>,
);

const Filter = I(<path d="M2 4h16l-6 6.5V15l-4 2V10.5z" />);

const Sort = I(
	<>
		<path d="M5 4v12M5 16l-3-3M5 16l3-3" />
		<path d="M15 16V4M15 4l-3 3M15 4l3 3" />
	</>,
);

const MoreH = I(
	<>
		<circle cx="4" cy="10" r="1.2" fill="currentColor" stroke="none" />
		<circle cx="10" cy="10" r="1.2" fill="currentColor" stroke="none" />
		<circle cx="16" cy="10" r="1.2" fill="currentColor" stroke="none" />
	</>,
);

const MoreV = I(
	<>
		<circle cx="10" cy="4" r="1.2" fill="currentColor" stroke="none" />
		<circle cx="10" cy="10" r="1.2" fill="currentColor" stroke="none" />
		<circle cx="10" cy="16" r="1.2" fill="currentColor" stroke="none" />
	</>,
);

/* ────────────────────────────────────────────
 * Статус
 * ──────────────────────────────────────────── */

const Check = I(<path d="M4 10.5l4 4 8-8.5" />);

const AlertCircle = I(
	<>
		<circle cx="10" cy="10" r="8" />
		<path d="M10 6v5" />
		<circle cx="10" cy="14" r="0.5" fill="currentColor" />
	</>,
);

const AlertTriangle = I(
	<>
		<path d="M10 3L1 17h18z" />
		<path d="M10 8v4" />
		<circle cx="10" cy="14.5" r="0.5" fill="currentColor" />
	</>,
);

const Info = I(
	<>
		<circle cx="10" cy="10" r="8" />
		<path d="M10 9v5" />
		<circle cx="10" cy="6.5" r="0.5" fill="currentColor" />
	</>,
);

const CheckCircle = I(
	<>
		<circle cx="10" cy="10" r="8" />
		<path d="M6.5 10l2.5 2.5 5-5" />
	</>,
);

const XCircle = I(
	<>
		<circle cx="10" cy="10" r="8" />
		<path d="M7 7l6 6M13 7L7 13" />
	</>,
);

/* ────────────────────────────────────────────
 * Объекты
 * ──────────────────────────────────────────── */

const Shield = I(<path d="M10 2L3 5.5v5C3 15 6 18 10 18s7-3 7-7.5v-5z" />);

const ShieldCheck = I(
	<>
		<path d="M10 2L3 5.5v5C3 15 6 18 10 18s7-3 7-7.5v-5z" />
		<path d="M7 10l2 2 4-4" />
	</>,
);

const Database = I(
	<>
		<ellipse cx="10" cy="5" rx="7" ry="3" />
		<path d="M3 5v10c0 1.7 3.1 3 7 3s7-1.3 7-3V5" />
		<path d="M3 10c0 1.7 3.1 3 7 3s7-1.3 7-3" />
	</>,
);

const Server = I(
	<>
		<rect x="3" y="3" width="14" height="5" rx="1" />
		<rect x="3" y="12" width="14" height="5" rx="1" />
		<circle cx="6" cy="5.5" r="0.8" fill="currentColor" />
		<circle cx="6" cy="14.5" r="0.8" fill="currentColor" />
	</>,
);

const Globe = I(
	<>
		<circle cx="10" cy="10" r="8" />
		<path d="M2 10h16" />
		<path d="M10 2c2.5 2.5 4 5 4 8s-1.5 5.5-4 8" />
		<path d="M10 2c-2.5 2.5-4 5-4 8s1.5 5.5 4 8" />
	</>,
);

const Key = I(
	<>
		<circle cx="7" cy="13" r="4" />
		<path d="M10 10l6-6" />
		<path d="M14 4l2 2" />
		<path d="M12 6l2 2" />
	</>,
);

const Lock = I(
	<>
		<rect x="4" y="9" width="12" height="8" rx="1" />
		<path d="M7 9V6a3 3 0 016 0v3" />
	</>,
);

const Unlock = I(
	<>
		<rect x="4" y="9" width="12" height="8" rx="1" />
		<path d="M7 9V6a3 3 0 016 0" />
	</>,
);

const Eye = I(
	<>
		<path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z" />
		<circle cx="10" cy="10" r="3" />
	</>,
);

const EyeOff = I(
	<>
		<path d="M2 2l16 16" />
		<path d="M6.7 6.7A7.6 7.6 0 002 10s3 6 8 6c1.4 0 2.7-.4 3.8-1" />
		<path d="M11 4.1A7.8 7.8 0 0118 10a12 12 0 01-2.2 3.2" />
	</>,
);

const Link = I(
	<>
		<path d="M8 12l4-4" />
		<path d="M11.5 14.5l2-2a3.5 3.5 0 000-5l-1-1a3.5 3.5 0 00-5 0l-2 2" />
		<path d="M8.5 5.5l-2 2a3.5 3.5 0 000 5l1 1a3.5 3.5 0 005 0l2-2" />
	</>,
);

/* ────────────────────────────────────────────
 * Люди
 * ──────────────────────────────────────────── */

const User = I(
	<>
		<circle cx="10" cy="7" r="4" />
		<path d="M3 18c0-3.3 3.1-6 7-6s7 2.7 7 6" />
	</>,
);

const Users = I(
	<>
		<circle cx="8" cy="7" r="3.5" />
		<path d="M1.5 18c0-3 2.9-5.5 6.5-5.5s6.5 2.5 6.5 5.5" />
		<circle cx="15" cy="7" r="2.5" />
		<path d="M15 12c2.5.5 4 2.2 4 4.5" />
	</>,
);

/* ────────────────────────────────────────────
 * UI
 * ──────────────────────────────────────────── */

const Bell = I(
	<>
		<path d="M10 17c1.1 0 2-.9 2-2H8c0 1.1.9 2 2 2z" />
		<path d="M16 12c0-3-1.5-5.5-4-6.3V5a2 2 0 00-4 0v.7C5.5 6.5 4 9 4 12l-1 3h14z" />
	</>,
);

const Calendar = I(
	<>
		<rect x="3" y="4" width="14" height="13" rx="1" />
		<path d="M3 8h14" />
		<path d="M7 2v3M13 2v3" />
	</>,
);

const Clock = I(
	<>
		<circle cx="10" cy="10" r="8" />
		<path d="M10 5v5l3.5 2" />
	</>,
);

const Settings = I(
	<>
		<circle cx="10" cy="10" r="2.5" />
		<path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.2 4.2l1.5 1.5M14.3 14.3l1.5 1.5M4.2 15.8l1.5-1.5M14.3 5.7l1.5-1.5" />
	</>,
);

const Terminal = I(
	<>
		<rect x="2" y="3" width="16" height="14" rx="2" />
		<path d="M5 8l3 3-3 3" />
		<path d="M11 14h4" />
	</>,
);

/* ────────────────────────────────────────────
 * Данные / Визуализация
 * ──────────────────────────────────────────── */

const BarChart = I(
	<>
		<path d="M3 17h14" />
		<rect x="5" y="8" width="2" height="9" rx="0.5" />
		<rect x="9" y="4" width="2" height="13" rx="0.5" />
		<rect x="13" y="10" width="2" height="7" rx="0.5" />
	</>,
);

const PieChart = I(
	<>
		<circle cx="10" cy="10" r="8" />
		<path d="M10 2v8h8" />
	</>,
);

const Activity = I(<path d="M2 10h3l2-6 3 12 2-6h6" />);

const Layers = I(
	<>
		<path d="M10 2L2 7l8 5 8-5z" />
		<path d="M2 13l8 5 8-5" />
		<path d="M2 10l8 5 8-5" />
	</>,
);

const MapIcon = I(
	<>
		<path d="M3 5l5-2 4 2 5-2v14l-5 2-4-2-5 2z" />
		<path d="M8 3v14M12 5v14" />
	</>,
);

/* ────────────────────────────────────────────
 * Масштабирование
 * ──────────────────────────────────────────── */

const ZoomIn = I(
	<>
		<circle cx="9" cy="9" r="6" />
		<path d="M14 14l4 4" />
		<path d="M9 6v6M6 9h6" />
	</>,
);

const ZoomOut = I(
	<>
		<circle cx="9" cy="9" r="6" />
		<path d="M14 14l4 4" />
		<path d="M6 9h6" />
	</>,
);

const FitScreen = I(
	<>
		<path d="M3 7V4a1 1 0 011-1h3" />
		<path d="M13 3h3a1 1 0 011 1v3" />
		<path d="M17 13v3a1 1 0 01-1 1h-3" />
		<path d="M7 17H4a1 1 0 01-1-1v-3" />
		<rect x="6" y="6" width="8" height="8" rx="1" />
	</>,
);

/* ────────────────────────────────────────────
 * Документы / Отчёты
 * ──────────────────────────────────────────── */

const FileText = I(
	<>
		<path d="M14 2H6a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V2z" />
		<path d="M14 2v4h4" fill="none" />
		<path d="M7 13h6M7 9h6M7 6h3" />
	</>,
);

/* ────────────────────────────────────────────
 * Флаги
 * ──────────────────────────────────────────── */

/**
 * Хелпер для флагов. В отличие от I(), не задаёт stroke и fill="none":
 * флаг рисуется заливками. Соотношение сторон 2:1, поэтому высота —
 * половина заданного размера.
 *
 * Эмодзи-флаги не используются: правилами проекта эмодзи запрещены, и на
 * Windows флаговые эмодзи не отрисовываются вовсе — вместо флага виден
 * двухбуквенный код.
 */
const Flag = (paths: React.ReactNode): React.FC<IconProps> => {
	const FlagComponent: React.FC<IconProps> = ({ size = 20, style, ...props }) => (
		<svg
			width={size}
			height={size / 2}
			viewBox="0 0 60 30"
			style={{ flexShrink: 0, ...style }}
			{...props}
		>
			{paths}
			{/* Белые полосы иначе сливаются со светлым фоном карточки. Рамка
			    лежит на краю viewBox, и наружную половину обводки срезает
			    область просмотра: видимая толщина -- 2 единицы из 4 */}
			<rect width="60" height="30" fill="none" stroke="rgba(0,0,0,0.15)" strokeWidth="4" />
		</svg>
	);
	return FlagComponent;
};

const FlagRu = Flag(
	<>
		<rect width="60" height="10" y="0" fill="#ffffff" />
		<rect width="60" height="10" y="10" fill="#0039a6" />
		<rect width="60" height="10" y="20" fill="#d52b1e" />
	</>,
);

const FlagEn = Flag(
	<>
		<rect width="60" height="30" fill="#012169" />
		<path d="M0,0 L60,30 M60,0 L0,30" stroke="#ffffff" strokeWidth="6" />
		<path d="M0,0 L60,30 M60,0 L0,30" stroke="#c8102e" strokeWidth="4" />
		<path d="M30,0 V30 M0,15 H60" stroke="#ffffff" strokeWidth="10" />
		<path d="M30,0 V30 M0,15 H60" stroke="#c8102e" strokeWidth="6" />
	</>,
);

/* ────────────────────────────────────────────
 * Специальный компонент — точка-индикатор
 * ──────────────────────────────────────────── */

const Dot: React.FC<DotProps> = ({ size = 6, color = 'currentColor', glow, style, ...props }) => (
	<span
		style={{
			display: 'inline-block',
			width: size,
			height: size,
			borderRadius: '50%',
			background: color,
			flexShrink: 0,
			boxShadow: glow ? `0 0 4px ${color}50` : undefined,
			...style,
		}}
		{...props}
	/>
);

/* ────────────────────────────────────────────
 * Единый экспорт библиотеки иконок
 * ──────────────────────────────────────────── */

export const Icon = {
	/* Навигация */
	Search,
	Close,
	Menu,
	ChevronDown,
	ChevronUp,
	ChevronRight,
	ChevronLeft,
	ArrowUp,
	ArrowDown,
	ArrowRight,
	ArrowLeft,
	ExternalLink,

	/* Действия */
	Plus,
	Minus,
	Edit,
	Trash,
	Copy,
	Download,
	Upload,
	Refresh,
	Filter,
	Sort,
	MoreH,
	MoreV,

	/* Статус */
	Check,
	AlertCircle,
	AlertTriangle,
	Info,
	CheckCircle,
	XCircle,

	/* Объекты */
	Shield,
	ShieldCheck,
	Database,
	Server,
	Globe,
	Key,
	Lock,
	Unlock,
	Eye,
	EyeOff,
	Link,

	/* Люди */
	User,
	Users,

	/* UI */
	Bell,
	Calendar,
	Clock,
	Settings,
	Terminal,

	/* Данные / Визуализация */
	BarChart,
	PieChart,
	Activity,
	Layers,
	Map: MapIcon,

	/* Масштабирование */
	ZoomIn,
	ZoomOut,
	FitScreen,

	/* Документы */
	FileText,

	/* Флаги */
	FlagRu,
	FlagEn,

	/* Специальные */
	Dot,
} as const;
